import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, ShieldOff, ShieldCheck, History, Pencil, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { UsuarioDialog } from "@/components/acessos/UsuarioDialog";
import { HistoricoDialog } from "@/components/acessos/HistoricoDialog";
import { GruposPanel } from "@/components/acessos/GruposPanel";

function Badges({ u, p = "" }) {
  return (
    <div className="flex flex-wrap gap-1">
      <span data-testid={`${p}usuario-situacao-${u.id}`} className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${u.ativo ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
        {u.ativo ? "Ativo" : "Bloqueado"}</span>
      {u.protegido && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#083A6B]/10 text-[#083A6B]" data-testid={`${p}usuario-protegido-${u.id}`}>Lista de segurança</span>}
      {u.fora_da_lista_seguranca && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-700">Removido da lista de segurança</span>}
      {u.conta_externa && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800" data-testid={`${p}usuario-externa-${u.id}`}>Conta externa{u.conta_externa_produto ? ` · ${u.conta_externa_produto}` : ""}</span>}
      {!u.dominio_institucional && <span className="text-[10px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">Fora do domínio institucional</span>}
    </div>
  );
}

function Acoes({ u, onEdit, onHist, onToggle, p = "" }) {
  return (
    <div className="flex gap-1 justify-end">
      <Button size="icon" variant="ghost" title="Editar" data-testid={`${p}usuario-btn-editar-${u.id}`} onClick={() => onEdit(u)}><Pencil size={15} /></Button>
      <Button size="icon" variant="ghost" title="Histórico" data-testid={`${p}usuario-btn-historico-${u.id}`} onClick={() => onHist(u)}><History size={15} /></Button>
      <Button size="icon" variant="ghost" title={u.ativo ? "Bloquear" : "Reativar"} disabled={u.ativo && u.protegido}
        data-testid={`${p}usuario-btn-${u.ativo ? "bloquear" : "reativar"}-${u.id}`} onClick={() => onToggle(u)}>
        {u.ativo ? <ShieldOff size={15} className="text-red-700" /> : <ShieldCheck size={15} className="text-emerald-700" />}
      </Button>
    </div>
  );
}

export default function UsuariosPermissoes() {
  const [meta, setMeta] = useState(null);
  const [users, setUsers] = useState([]);
  const [filtros, setFiltros] = useState({ q: "", perfil: "todos", situacao: "todos" });
  const [dlg, setDlg] = useState({ open: false, editing: null });
  const [hist, setHist] = useState(null);

  const nomeUnidades = (u) => (!u.pousada_ids?.length ? "Todas as unidades"
    : u.pousada_ids.map((id) => meta?.pousadas.find((p) => p.id === id)?.nome || "—").join(", "));

  const load = useCallback(async () => {
    const params = { q: filtros.q || undefined, perfil: filtros.perfil !== "todos" ? filtros.perfil : undefined,
      situacao: filtros.situacao !== "todos" ? filtros.situacao : undefined };
    try {
      const [m, u] = await Promise.all([api.get("/acessos/meta"), api.get("/acessos/usuarios", { params })]);
      setMeta(m.data); setUsers(u.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Falha ao carregar usuários"); }
  }, [filtros]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const onToggle = async (u) => {
    if (u.ativo && !window.confirm(`Bloquear o acesso de ${u.nome}? A sessão atual será encerrada.`)) return;
    try {
      await api.post(`/acessos/usuarios/${u.id}/${u.ativo ? "bloquear" : "reativar"}`);
      toast.success(u.ativo ? "Acesso bloqueado" : "Acesso reativado");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Falha ao alterar situação"); }
  };
  const onEdit = (u) => setDlg({ open: true, editing: u });

  return (
    <div data-testid="usuarios-permissoes-page">
      <PageHeader eyebrow="Controle de acesso" title="Usuários e Permissões"
        description="Gerencie quem acessa o painel, com perfil, unidades e permissões. Colaboradoras da Limpeza continuam no cadastro próprio."
        actions={<Button data-testid="btn-novo-usuario" className="bg-[#083A6B] hover:bg-[#062C52]"
          onClick={() => setDlg({ open: true, editing: null })}><Plus size={16} className="mr-1" />Novo usuário</Button>} />

      {meta?.escopo === "plataforma" && (
        <div className="mb-6 flex flex-wrap items-center gap-3 text-sm bg-white border border-stone-200 rounded-lg p-4" data-testid="aviso-limpeza">
          <Sparkles size={16} className="text-[#D4A652]" />
          <span>Limpeza: {meta.limpeza_total ?? 0} colaborador(es) no cadastro independente.</span>
          <Link to="/admin/usuarios-limpeza" className="text-[#083A6B] underline" data-testid="link-usuarios-limpeza">Abrir Usuários da Limpeza</Link>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input className="pl-9" placeholder="Pesquisar por nome ou e-mail" value={filtros.q} data-testid="usuarios-busca"
            onChange={(e) => setFiltros({ ...filtros, q: e.target.value })} />
        </div>
        <Select value={filtros.perfil} onValueChange={(v) => setFiltros({ ...filtros, perfil: v })}>
          <SelectTrigger className="md:w-64" data-testid="usuarios-filtro-perfil"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os perfis</SelectItem>
            {(meta?.perfis || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filtros.situacao} onValueChange={(v) => setFiltros({ ...filtros, situacao: v })}>
          <SelectTrigger className="md:w-44" data-testid="usuarios-filtro-situacao"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todas as situações</SelectItem>
            <SelectItem value="ativo">Ativos</SelectItem>
            <SelectItem value="bloqueado">Bloqueados</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="hidden md:block bg-white border border-stone-200 rounded-lg overflow-x-auto">
        <Table data-testid="usuarios-tabela">
          <TableHeader><TableRow>
            <TableHead>Usuário</TableHead><TableHead>Perfil</TableHead><TableHead>Unidades</TableHead>
            <TableHead>Situação</TableHead><TableHead className="text-right">Ações</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`usuario-row-${u.id}`}>
                <TableCell><div className="font-medium text-stone-900">{u.nome}</div><div className="text-xs text-stone-500">{u.email}</div></TableCell>
                <TableCell className="text-sm">{u.perfil_label}</TableCell>
                <TableCell className="text-xs text-stone-600 max-w-xs">{nomeUnidades(u)}</TableCell>
                <TableCell><Badges u={u} /></TableCell>
                <TableCell><Acoes u={u} onEdit={onEdit} onHist={setHist} onToggle={onToggle} /></TableCell>
              </TableRow>
            ))}
            {!users.length && <TableRow><TableCell colSpan={5} className="text-center text-stone-500 py-8" data-testid="usuarios-vazio">Nenhum usuário encontrado.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <div className="md:hidden space-y-3">
        {users.map((u) => (
          <div key={u.id} className="bg-white border border-stone-200 rounded-lg p-4" data-testid={`usuario-card-${u.id}`}>
            <div className="flex justify-between gap-2">
              <div className="min-w-0"><div className="font-medium text-stone-900 truncate">{u.nome}</div>
                <div className="text-xs text-stone-500 truncate">{u.email}</div></div>
              <Acoes u={u} p="m-" onEdit={onEdit} onHist={setHist} onToggle={onToggle} />
            </div>
            <div className="text-xs text-stone-700 mt-2">{u.perfil_label}</div>
            <div className="text-xs text-stone-500 mt-1">{nomeUnidades(u)}</div>
            <div className="mt-2"><Badges u={u} p="m-" /></div>
          </div>
        ))}
        {!users.length && <div className="text-center text-stone-500 py-8 text-sm">Nenhum usuário encontrado.</div>}
      </div>

      {meta?.escopo === "plataforma" && <GruposPanel meta={meta} onChanged={load} />}

      <UsuarioDialog open={dlg.open} editing={dlg.editing} meta={meta} onSaved={load}
        onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))} />
      <HistoricoDialog usuario={hist} perfis={meta?.perfis || []} onClose={() => setHist(null)} />
    </div>
  );
}
